import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Card, Badge, ProgressBar } from "react-bootstrap";
import {
  BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Legend
} from "recharts";
import {
  FaBriefcase,
  FaUsers,
  FaChartBar,
  FaExclamationTriangle
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { fetchTimeDistributionData } from "../../../redux/slices/timeDistributionSlice";

const OverviewTab = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { allocations, directory, isLoading } = useSelector((state) => state.timeDistribution);

  useEffect(() => {
    dispatch(fetchTimeDistributionData());
  }, [dispatch]);

  const financedProjects = useMemo(() => {
    return (allocations || []).filter((p) => p.is_financed);
  }, [allocations]);

  const conflictDetails = useMemo(() => {
    const userEtiMap = new Map();

    financedProjects.forEach(proj => {
      const members = proj.resources || proj.members || proj.users || proj.memberships || [];
      
      members.forEach(m => {
        if (m.has_conflict) {
          const userObj = m.user || m;
          const id = userObj.id || m.user_id || m.userId || m.id;
          
          if (id) {
            if (!userEtiMap.has(id)) {
              const name = `${userObj.first_name || ""} ${userObj.last_name || ""}`.trim() || userObj.name || t('components.overviewTab.unknown_user');
              const eti = Number(m.total_eti || m.capacity || m.eti) || 0;
              
              userEtiMap.set(id, { id, name, eti, projects: [] });
            }
            
            const userData = userEtiMap.get(id);
            const projName = proj.name || t('components.overviewTab.unknown_project');
            if (!userData.projects.includes(projName)) {
              userData.projects.push(projName);
            }
          }
        }
      });
    });

    return Array.from(userEtiMap.values())
      .map(user => {
        const overage = Math.max(0, user.eti - 1.0);
        return {
          id: user.id,
          name: user.name,
          eti: Number(user.eti.toFixed(2)),
          safeEti: 1.0,
          excessEti: Number(overage.toFixed(2)),
          overage: Number(overage.toFixed(2)),
          projectNames: user.projects.join(", "),
        };
      })
      .sort((a, b) => b.overage - a.overage);
  }, [financedProjects, t]);

  const topConflicts = useMemo(() => {
    return conflictDetails.slice(0, 10);
  }, [conflictDetails]);

  const computedOverview = useMemo(() => {
    const totalEmployees = directory ? directory.length : 0;
    const activeFinancedProjects = financedProjects.length;

    const totalFinancedEti = financedProjects.reduce(
      (sum, p) => sum + (Number(p.allocated_eti || p.total_allocated) || 0),
      0
    );

    const totalFinancedTarget = financedProjects.reduce(
      (sum, p) => sum + (Number(p.total_eti || p.max_eti) || 0),
      0
    );

    return {
      activeProjectsCount: activeFinancedProjects,
      totalEmployeesCount: totalEmployees,
      allocatedEti: totalFinancedEti.toFixed(2),
      targetEti: totalFinancedTarget.toFixed(2),
      conflictsCount: conflictDetails.length,
    };
  }, [financedProjects, directory, conflictDetails]);

  const financedChartData = useMemo(() => {
    return financedProjects.map((p) => {
      const allocated = Number((p.allocated_eti || p.total_allocated || 0).toFixed(2));
      const target = Number((p.total_eti || p.max_eti || 0).toFixed(2));
      const percentage = target > 0 ? Math.round((allocated / target) * 100) : 0;

      return {
        id: p.id,
        name: p.name || t('components.overviewTab.unnamed'),
        allocated: allocated,
        target: target,
        percentage,
      };
    });
  }, [financedProjects, t]);

  const ProjectTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-surface p-3 atlar-card shadow-sm" style={{ border: 'var(--border-thin)' }}>
          <p className="fw-bold mb-1 txt-main">{label}</p>
          <p className="mb-0 txt-success" style={{ fontSize: "var(--text-small)" }}>{t('components.overviewTab.tooltip.allocated')}: {data.allocated} ETI</p>
          <p className="mb-0 txt-subtle" style={{ fontSize: "var(--text-small)" }}>{t('components.overviewTab.tooltip.ceiling')}: {data.target} ETI</p>
          <hr className="my-2" style={{ borderColor: 'var(--gray-300)' }} />
          <p className="mb-0 txt-main fw-bold" style={{ fontSize: "var(--text-small)" }}>{t('components.overviewTab.tooltip.filling')}: {data.percentage}%</p>
        </div>
      );
    }
    return null;
  };

  const ConflictTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-surface p-3 atlar-card shadow-sm" style={{ border: 'var(--border-thin)' }}>
          <p className="fw-bold mb-1 txt-main">{label}</p>
          <p className="mb-0 txt-success" style={{ fontSize: "var(--text-small)" }}>{t('components.overviewTab.tooltip.base_capacity')}: 1.00 ETI</p>
          <p className="mb-0 txt-danger fw-bold" style={{ fontSize: "var(--text-small)" }}>{t('components.overviewTab.tooltip.excess')}: +{data.excessEti} ETI</p>
          <hr className="my-2" style={{ borderColor: 'var(--gray-300)' }} />
          <p className="mb-0 txt-main fw-bold" style={{ fontSize: "var(--text-small)" }}>{t('components.overviewTab.tooltip.total_allocated')}: {data.eti} ETI</p>
        </div>
      );
    }
    return null;
  };

  if (isLoading && (!allocations || allocations.length === 0)) {
    return <div className="p-4 text-center txt-subtle">{t('components.overviewTab.loading')}</div>;
  }

  return (
    <div className="fade-in">
      <Row className="g-3 mb-4">
        <Col md={3}>
          <Card className="atlar-card p-3 h-100">
            <div className="d-flex justify-content-between align-items-center h-100">
              <div>
                <div className="txt-subtle mb-1" style={{ fontSize: "var(--text-small)", fontWeight: 600 }}>{t('components.overviewTab.stats.financed_projects')}</div>
                <h3 className="fw-bold mb-0 txt-main">{computedOverview.activeProjectsCount}</h3>
              </div>
              <div className="d-flex align-items-center justify-content-center rounded" style={{ width: "45px", height: "45px", backgroundColor: "var(--gray-100)", color: "var(--brand-primary)" }}>
                <FaBriefcase size={20} />
              </div>
            </div>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="atlar-card p-3 h-100">
            <div className="d-flex justify-content-between align-items-center h-100">
              <div>
                <div className="txt-subtle mb-1" style={{ fontSize: "var(--text-small)", fontWeight: 600 }}>{t('components.overviewTab.stats.total_employees')}</div>
                <h3 className="fw-bold mb-0 txt-main">{computedOverview.totalEmployeesCount}</h3>
              </div>
              <div className="d-flex align-items-center justify-content-center rounded" style={{ width: "45px", height: "45px", backgroundColor: "var(--gray-100)", color: "var(--gray-600)" }}>
                <FaUsers size={22} />
              </div>
            </div>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="atlar-card p-3 h-100">
            <div className="d-flex justify-content-between align-items-center h-100">
              <div>
                <div className="txt-subtle mb-1" style={{ fontSize: "var(--text-small)", fontWeight: 600 }}>{t('components.overviewTab.stats.total_financed_eti')}</div>
                <h3 className="fw-bold mb-0 txt-main">
                  {computedOverview.allocatedEti}{" "}
                  <span className="txt-subtle" style={{ fontSize: "1rem" }}>
                    / {computedOverview.targetEti}
                  </span>
                </h3>
              </div>
              <div className="d-flex align-items-center justify-content-center rounded" style={{ width: "45px", height: "45px", backgroundColor: "var(--gray-100)", color: "var(--brand-primary)" }}>
                <FaChartBar size={20} />
              </div>
            </div>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="atlar-card p-3 h-100" style={{ borderLeft: "4px solid var(--danger-red)" }}>
            <div className="d-flex justify-content-between align-items-center h-100">
              <div>
                <div className="txt-danger mb-1" style={{ fontSize: "var(--text-small)", fontWeight: 600 }}>{t('components.overviewTab.stats.conflicts')}</div>
                <h3 className="fw-bold mb-0 txt-danger">{computedOverview.conflictsCount}</h3>
              </div>
              <div className="d-flex align-items-center justify-content-center rounded" style={{ width: "45px", height: "45px", backgroundColor: "rgba(239, 68, 68, 0.1)", color: "var(--danger-red)" }}>
                <FaExclamationTriangle size={20} />
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Row className="g-4 mb-4">
        <Col lg={6}>
          <Card className="atlar-card p-4 h-100">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="fw-bold txt-main mb-0" style={{ fontSize: "var(--text-h4)" }}>{t('components.overviewTab.charts.allocation_title')}</h5>
              <Badge bg="success" className="fw-normal rounded-pill px-3" style={{ backgroundColor: 'var(--success-green)' }}>{t('components.overviewTab.charts.financed_badge')}</Badge>
            </div>
            {financedChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={financedChartData} margin={{ top: 10, right: 10, left: 0, bottom: 25 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--gray-200)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--gray-600)" }} angle={-15} textAnchor="end" interval={0} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "var(--gray-600)" }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ProjectTooltip />} cursor={{ fill: "var(--gray-50)" }} />
                  <Legend wrapperStyle={{ fontSize: "var(--text-small)", paddingTop: "5px" }} />
                  <Bar dataKey="allocated" name={t('components.overviewTab.legend.allocated_eti')} fill="var(--success-green)" radius={[4, 4, 0, 0]} maxBarSize={40} />
                  <Bar dataKey="target" name={t('components.overviewTab.legend.project_ceiling')} fill="var(--gray-200)" radius={[4, 4, 0, 0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-center txt-subtle">
                <p className="mb-0">{t('components.overviewTab.charts.no_financed_projects')}</p>
              </div>
            )}
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="atlar-card p-4 h-100">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="fw-bold txt-main mb-0" style={{ fontSize: "var(--text-h4)" }}>
                {conflictDetails.length > 10 ? t('components.overviewTab.charts.top_10_conflicts') : t('components.overviewTab.charts.users_in_conflict')}
              </h5>
              <Badge bg="danger" className="fw-normal rounded-pill px-3" style={{ backgroundColor: 'var(--danger-red)' }}>{conflictDetails.length}</Badge>
            </div>
            {topConflicts.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={topConflicts} layout="vertical" margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="var(--gray-200)" />
                  <XAxis type="number" domain={[0, 'auto']} tick={{ fontSize: 11, fill: "var(--gray-600)" }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "var(--gray-600)", fontWeight: 500 }} interval={0} axisLine={false} tickLine={false} width={130} />
                  <Tooltip content={<ConflictTooltip />} cursor={{ fill: "var(--gray-50)" }} />
                  <Legend wrapperStyle={{ fontSize: "var(--text-small)" }} />
                  <ReferenceLine x={1} stroke="var(--danger-red)" strokeDasharray="4 4" label={{ value: "Max 1.0", position: "insideBottomRight", fill: "var(--danger-red)", fontSize: 10, fontWeight: "bold" }} />
                  <Bar dataKey="safeEti" name={t('components.overviewTab.legend.base_capacity')} stackId="a" fill="var(--success-green)" radius={[0, 0, 0, 0]} maxBarSize={28} />
                  <Bar dataKey="excessEti" name={t('components.overviewTab.legend.excess_conflict')} stackId="a" fill="var(--danger-red)" radius={[0, 4, 4, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-center txt-success">
                <div>
                  <FaExclamationTriangle size={32} className="mb-3" opacity={0.5} />
                  <p className="mb-0 fw-semibold" style={{ fontSize: "1.1rem" }}>{t('components.overviewTab.charts.no_conflicts')}</p>
                  <small className="txt-subtle">{t('components.overviewTab.charts.no_conflicts_desc')}</small>
                </div>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Row className="g-4">
        <Col lg={12}>
          <Card className="atlar-card p-4">
            <h5 className="fw-bold txt-main mb-3" style={{ fontSize: "var(--text-h4)" }}>{t('components.overviewTab.details.title')}</h5>
            {financedChartData.length > 0 ? (
              <div className="d-flex flex-column gap-3">
                {financedChartData.map((proj) => (
                  <div key={proj.id} className="p-3 rounded-3" style={{ backgroundColor: 'var(--gray-50)' }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-bold txt-main">{proj.name}</span>
                      <span className="txt-subtle small fw-semibold">
                        {t('components.overviewTab.details.allocated')} <strong className="txt-success">{proj.allocated}</strong> / {t('components.overviewTab.details.target')} <strong className="txt-main">{proj.target} ETI</strong> ({proj.percentage}%)
                      </span>
                    </div>
                    <ProgressBar 
                      now={Math.min(proj.percentage, 100)} 
                      variant={proj.percentage > 100 ? "danger" : "success"} 
                      style={{ height: "8px", backgroundColor: 'var(--gray-200)' }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <p className="txt-subtle mb-0">{t('components.overviewTab.details.no_projects_registered')}</p>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default OverviewTab;